import API, { JFrogTokenCreateRequest } from "@/api/api";
import { toast } from "@/lib/instrumented-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const api = new API();

export function useJFrogTokens(orgId?: string, includeRevoked = false) {
  return useQuery({
    queryKey: ["jfrogTokens", orgId, includeRevoked],
    queryFn: () => api.listJFrogTokens(orgId!, includeRevoked),
    enabled: !!orgId,
  });
}

export function useRevokeJFrogToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, tokenId }: { orgId: string; tokenId: string }) => {
      return await api.revokeJFrogToken(orgId, tokenId);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["jfrogTokens", variables.orgId] });
      toast.success("JFrog token revoked successfully");
    },
    onError: (error, variables) => {
      const is404 =
        error && typeof error === "object" && "response" in error
          ? (error as { response?: { status?: number } }).response?.status === 404
          : false;
      if (is404) {
        queryClient.invalidateQueries({ queryKey: ["jfrogTokens", variables.orgId] });
        toast.success("Token already removed");
        return;
      }
      const errorMessage = error instanceof Error ? error.message : "Failed to revoke JFrog token";
      toast.error(errorMessage);
    },
  });
}

export function useCreateJFrogToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, request }: { orgId: string; request: JFrogTokenCreateRequest }) => {
      return await api.createJFrogToken(orgId, request);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["jfrogTokens", variables.orgId] });
      toast.success("JFrog token created successfully");
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Failed to create JFrog token";
      toast.error(errorMessage);
    },
  });
}
